import React from 'react';
import BaseTopicLayout from './BaseTopicLayout.js'; // Ensure the path is correct

/**
 * CookiePolicyPage Component.
 * Hosts the content of the Cookie Policy within the common topic layout.
 *
 * @param {function} [props.setHasAcceptedTrackingCookies=()=>{}] - Setter for the global tracking cookies state.
 * @param {boolean} [props.hasAcceptedTrackingCookies=true] - Global tracking cookies state.
 */
function CookiePolicyPage({ 
    setHasAcceptedTrackingCookies = () => {}, 
    hasAcceptedTrackingCookies = true 
}) {
    // The Cookie Policy content as a string.
    // The text has been modified to address the visitor directly.
    const cookiePolicyContent = `
        This Cookie Policy explains what cookies are, how and why they are used on the website https://zapdesign.top/ (hereinafter, the "Site"). We recommend reading this policy to understand the type of information collected using cookies and how such information is used.

        What are Cookies?
        Cookies are small text files that are stored on your computer or mobile device when you visit a website. They are widely used to make websites work, or to work more efficiently, as well as to provide information to the site owners.

        Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your personal computer or mobile device even when you go offline, while session cookies are deleted as soon as you close your web browser.

        How Cookies Are Used
        The Site https://zapdesign.top/ uses cookies for the following purposes:

        1. Necessary Cookies (Technical)
        These cookies are essential to enable you to navigate the site and use its features, such as accessing protected areas or managing navigation preferences. Without these cookies, some parts of the site may not function correctly. These cookies do not collect information about you that could be used for marketing or to remember where you have browsed on the internet.

        2. Preference Cookies (Functional)
        These cookies allow our website to remember the choices you make (such as your language or the region you are in) and provide enhanced, more personalized features. For example, they may be used to remember your viewing preferences or to prevent you from being asked to fill out forms you have already completed. The Site uses a cookie (myWebsiteCookieConsent, managed via js-cookie and react-cookie-consent) to store your preferences regarding consent to the use of cookies themselves.

        3. Statistics Cookies (Analytical)
        These cookies collect information about how you use our website, such as which pages you visit most often or if you receive error messages from web pages. These cookies do not collect information that personally identifies you. All information collected by these cookies is aggregated and therefore anonymous. They are only used to improve how the website works. If the Site integrates services like Google Analytics, these will use cookies to collect anonymous data on traffic and site usage.

        4. Marketing and Third-Party Cookies (Profiling)
        These cookies are used to deliver advertisements more relevant to you and your interests. They can also be used to limit the number of times you see an advertisement and to measure the effectiveness of an advertising campaign. They are usually placed by advertising networks with the website operator's permission.

        In this specific case, the integration of content such as videos from YouTube (which load from youtube-nocookie.com for greater privacy, but can still set cookies) and the possible embedding of services like SoundCloud (as seen in SoundTopic.js) imply the use of third-party cookies by these platforms. The playback of this content is conditioned on the explicit acceptance of tracking cookies by the user, as managed by the consent system.

        Projects on platforms like Render (e.g., https://lissajous-patterns-generator.onrender.com/, https://eight08-style-drum-machine.onrender.com/) may in turn have their own mechanisms for cookie usage, depending on their configuration. It is the user's responsibility to check the privacy and cookie policies of such third-party services.

        Cookie Management
        You have the right to choose whether to accept or decline cookies. You can exercise your cookie preferences through the consent banner that appears on your first access or at any time by clicking on the "Manage Cookie Preferences" link in the Site's footer.

        Most web browsers allow some control of most cookies through browser settings. To learn more about cookies, including how to see what cookies have been set and how to manage and delete them, visit www.allaboutcookies.org.

        Updates to the Cookie Policy
        We reserve the right to make changes to this Cookie Policy from time to time. We will inform users of any changes by publishing the new Cookie Policy on this page. We recommend reviewing this Cookie Policy periodically for any changes.

        Contact
        If you have any questions about this Cookie Policy, you can contact us using the details provided in our Privacy Policy.
    `.trim(); // Removes any extra spaces at the beginning/end

    return (
        <BaseTopicLayout
            title="Cookie Policy"
            descriptionContent="Information on how this website uses cookies."
            setHasAcceptedTrackingCookies={setHasAcceptedTrackingCookies}
            hasAcceptedTrackingCookies={hasAcceptedTrackingCookies}
        >
            <div style={{ 
                padding: '20px', 
                backgroundColor: 'rgba(0, 0, 0, 0.7)', 
                borderRadius: '8px', 
                color: '#e0e0e0',
                lineHeight: '1.6',
                maxWidth: '800px',
                margin: '20px auto',
                fontSize: '0.95em'
            }}>
                {/* Splits the text into paragraphs for better formatting */}
                {cookiePolicyContent.split('\n\n').map((paragraph, index) => (
                    <p key={index} style={{ marginBottom: '1em' }}>
                        {paragraph.split('\n').map((line, lineIndex) => (
                            <React.Fragment key={lineIndex}>
                                {line}
                                {lineIndex < paragraph.split('\n').length - 1 && <br />}
                            </React.Fragment>
                        ))}
                    </p>
                ))}
            </div>
            {/* Styles for policy pages. Consider moving them to a dedicated CSS file if they become numerous. */}
            <style jsx="true">{`
                .elementor-location-footer {
                    display: none; /* Hides the footer if you don't want it on these pages */
                }
                .main-content-container, body {
                    background-color: #111111 !important; /* Dark background for the site theme */
                }
            `}</style>
        </BaseTopicLayout>
    );
}

export default CookiePolicyPage;
